import React, { createContext, useContext, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleTemperatureUnit } from 'reducers/slices/temperatureSlice';

export type TemperatureUnit = 'C' | 'F';

interface TemperatureContextType {
  unit: TemperatureUnit;
  toggleUnit?: () => void;
  convertTemperature: (value: number, decimals?: number) => string;
  convertTemperatureValueOnly: (value: number, decimals?: number) => number;
  convertTemperatureUnitOnly: (value: number, decimals?: number) => string;
}

const TemperatureContext = createContext<TemperatureContextType | undefined>(undefined);

export const TemperatureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const unit = useSelector((state: any) => state.temperature?.unit || 'C');

  const toggleUnit = () => {
    dispatch(toggleTemperatureUnit());
  };

  const convertTemperature = (value: number, decimals = 2): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    if (unit === 'F') {
      const fahrenheit = parseFloat(((value * 9 / 5) + 32).toFixed(decimals));
      return `${fahrenheit}°F`;
    }
    const celsius = parseFloat(Number(value).toFixed(decimals));
    return `${celsius}°C`;
  };

  // New function that just converts the temperature value without the unit
  const convertTemperatureValueOnly = (value: number, decimals = 2): number => {
    if (value === null || value === undefined || isNaN(value)) return value;
    if (unit === 'F') {
      return parseFloat(((value * 9 / 5) + 32).toFixed(decimals));
    }
    return parseFloat(Number(value).toFixed(decimals));
  };

  const convertTemperatureUnitOnly = (value: number, decimals = 2): string => {
    if (value === null || value === undefined || isNaN(value)) return '';
    if (unit === 'F') {
      const fahrenheit = parseFloat(((value * 9 / 5) + 32).toFixed(decimals));
      return `${fahrenheit}°F`;
    }
    const celsius = parseFloat(Number(value).toFixed(decimals));
    return `${celsius}°C`;
  } 

  return (
    <TemperatureContext.Provider value={{ unit, toggleUnit, convertTemperature, convertTemperatureValueOnly, convertTemperatureUnitOnly }}>
      {children}
    </TemperatureContext.Provider>
  );
};

export const useTemperature = (): TemperatureContextType => {
  const context = useContext(TemperatureContext);
  
  if (context === undefined) {
    return {
      unit: 'C',
      toggleUnit: undefined,
      convertTemperature: (value: number, decimals = 2) => `${parseFloat(Number(value).toFixed(decimals))}°C`,
      convertTemperatureValueOnly: (value: number, decimals = 2) => parseFloat(Number(value).toFixed(decimals)),
      convertTemperatureUnitOnly: (value: number) => '°C'
    };
  }
  
  return context;
};
